import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, shareReplay, tap } from 'rxjs';
import { IDigitalProduct } from '../model/digital-product.model';
import { environment } from '../../environments/environment';
import { AuthenticationService } from '../../shared/services/authentication.service';

@Injectable({
  providedIn: 'root'
})
export class DigitalProductService {
  private http = inject(HttpClient);
  private authService = inject(AuthenticationService);
  private publicProductByIdCache = new Map<string, Observable<IDigitalProduct>>();
  private publicProductCache = new Map<string, Observable<IDigitalProduct>>();

  getPublicById(id: string): Observable<IDigitalProduct> {
    const encodedId = encodeURIComponent((id ?? '').trim());
    const cacheKey = encodedId;
    const cached = this.publicProductByIdCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const request$ = this.http.get<IDigitalProduct>(
      `${environment.API_URL}/api/DigitalProduct/public/${encodedId}`
    ).pipe(
      tap({
        error: () => this.publicProductByIdCache.delete(cacheKey)
      }),
      shareReplay(1)
    );

    this.publicProductByIdCache.set(cacheKey, request$);
    return request$;
  }

  getPublicByLoginAndSlug(login: string, slug: string): Observable<IDigitalProduct> {
    const encodedLogin = encodeURIComponent(login.trim());
    const encodedSlug = encodeURIComponent(slug.trim());
    const cacheKey = `${encodedLogin}::${encodedSlug}`;
    const cached = this.publicProductCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const request$ = this.http.get<IDigitalProduct>(
      `${environment.API_URL}/api/DigitalProduct/public/${encodedLogin}/${encodedSlug}`
    ).pipe(
      tap({
        error: () => this.publicProductCache.delete(cacheKey)
      }),
      shareReplay(1)
    );

    this.publicProductCache.set(cacheKey, request$);
    return request$;
  }

  purchase(digitalProductId: string, externalReference = ''): Observable<unknown> {
    const token: string | null = this.authService.getUserToken() ?? null;
    if (!token) {
      return of();
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });

    return this.http.post(
      `${environment.API_URL}/api/DigitalProduct/purchase`,
      {
        digitalProductId,
        externalReference
      },
      {
        headers
      }
    );
  }
}
